import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { reservationApi } from "@/lib/api";
import { clearActiveService } from "@/lib/services";

// Homepage-only presentation shell that routes to the existing reservation
// wizard at /reserve. If the visitor has no active draft (cold entry), we
// bootstrap an empty draft via the existing draft API before navigating —
// otherwise ReservationWizard's mount check bounces cold visitors back to
// `/`. If a draft already exists we just resume it.
export default function ReadyToBook() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // Synchronous re-entry lock — a React state closure lags the render, so
  // rapid synchronous clicks can all pass a state check. A ref updates
  // immediately and guarantees exactly one API path per click burst.
  const inFlight = useRef(false);

  const start = async () => {
    if (inFlight.current) return; // synchronous guard: no duplicate drafts
    inFlight.current = true;
    setBusy(true);
    try {
      // Unified booking entry: a generic "Start Your Reservation" click
      // must override any stale service/vehicle intent on an existing
      // draft so the customer doesn't inherit an abandoned Wedding /
      // Hourly / Airport session they didn't ask for.
      clearActiveService();
      const rec = await reservationApi.recover();
      // Generic entry from the landing "Start Your Reservation" must
      // override stale intent on any recovered draft. reset_fields nulls
      // service_type, vehicle, trip_type, and hourly settings so the
      // wizard opens on the Vehicle step regardless of what the customer
      // previously abandoned in a service-card session.
      const staleReset = [
        "selected_vehicle", "service_type", "trip_type",
        "hourly_duration", "ending_mode", "stops",
      ];
      await reservationApi.upsertDraft(
        rec?.reservation
          ? { reservation_id: rec.reservation.id, service_type: "", reset_fields: staleReset }
          : { service_type: "" }
      );
      navigate("/reserve");
    } catch (e) {
      toast.error("Could not start your reservation", {
        description: e?.response?.data?.detail || "Please try again.",
      });
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <section id="ready-to-book" data-testid="ready-to-book-section" className="px-4 pt-12 pb-2 sm:pt-16">
      <div className="mx-auto max-w-4xl text-center vb-panel p-8 sm:p-12">
        <Reveal>
          <h3 className="font-display font-bold uppercase tracking-[0.16em] text-[#d6d400] text-3xl sm:text-4xl leading-tight">
            Ready When You Are
          </h3>
          <p className="font-body text-[#f4f4ea] text-base sm:text-lg mt-4">
            Reserve your chauffeur in minutes.
          </p>
          <button
            type="button"
            onClick={start}
            disabled={busy}
            data-testid="ready-to-book-cta"
            aria-label="Start Your Reservation"
            className="vb-btn inline-flex items-center gap-2 mt-8 px-8 h-14 font-display uppercase tracking-[0.16em] text-sm text-[#050505] bg-[#d6d400] hover:bg-[#c0be00] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Starting…
              </>
            ) : (
              <>Start Your Reservation</>
            )}
          </button>
        </Reveal>
      </div>
    </section>
  );
}
